import { useEffect, useState } from "react";
import { getUserDetails } from "../services/network";

// Module-level so the cache and batching are shared across every component
// instance, not just within one — the same underwriter/processor id commonly
// shows up on many waivers on one page.
const nameCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

// Ids waiting on the next batched lookup, and the resolver for each one's
// promise. Batching (rather than one request per id) means a page rendering
// many waivers at once fires a single bulk request instead of N.
let pendingIds: string[] = [];
let pendingResolvers = new Map<string, (name: string) => void>();
let flushScheduled = false;
// The pending setTimeout's id, so a reset can cancel it outright — without
// this, a flush scheduled by one test but not yet fired can survive into
// the next test (which resets pendingIds/flushScheduled but can't stop an
// already-queued macrotask), then fire mid-way through that next test with
// pendingIds already emptied by its own beforeEach reset. That contributes
// a spurious extra getUserDetails([]) call before the new test's own
// legitimate batch goes out, inflating an expected single call into two —
// the actual names still resolve correctly, so only call-count assertions
// (not content) are affected. Production has no equivalent reset, so this
// only ever mattered for test isolation.
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Test-only escape hatch: this cache/queue is intentionally module-level/
// global, which would otherwise leak resolved names or pending batches
// across unrelated test cases that happen to reuse the same fixture id.
export const __resetUserDisplayNameCacheForTests = (): void => {
  nameCache.clear();
  inFlight.clear();
  pendingIds = [];
  pendingResolvers = new Map();
  flushScheduled = false;
  if (flushTimeoutId !== null) {
    clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }
};

const buildDisplayName = (data: any, fallback: string): string => {
  const name = [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim();
  return name || data?.username || fallback;
};

// Bounded retries for when the call itself fails (network error, timeout,
// 5xx) — distinct from a per-id "couldn't resolve" result, which the API
// returns successfully (see the error-entry shape handled below) and is
// already left uncached so it retries naturally on the next lookup. A
// wholesale call failure gets a few immediate attempts before falling back,
// rather than making every id in the batch wait for some later, unrelated
// re-render to trigger a fresh lookup.
const LOOKUP_RETRY_COUNT = 2;
const LOOKUP_RETRY_DELAY_MS = 150;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const fetchUserDetailsWithRetry = async (
  ids: string[],
  attemptsLeft: number = LOOKUP_RETRY_COUNT,
): Promise<any> => {
  try {
    return await getUserDetails(ids);
  } catch (err) {
    if (attemptsLeft <= 0) throw err;
    await delay(LOOKUP_RETRY_DELAY_MS);
    return fetchUserDetailsWithRetry(ids, attemptsLeft - 1);
  }
};

const flushPendingLookups = async (): Promise<void> => {
  const ids = pendingIds;
  const resolvers = pendingResolvers;
  pendingIds = [];
  pendingResolvers = new Map();
  flushScheduled = false;
  flushTimeoutId = null;

  // Nothing queued (e.g. this run was for a batch a test-reset already
  // cleared) — skip the network call entirely rather than firing one with
  // an empty id list.
  if (ids.length === 0) return;

  // async/try-catch (not .then/.catch) so this can't throw synchronously and
  // crash the caller — a badly-mocked or misconfigured network call should
  // fail soft to the raw id, same as everywhere else in this app.
  try {
    const { data } = await fetchUserDetailsWithRetry(ids);
    // /online-sizer/users doesn't key success and failure entries the same
    // way: a resolved profile has been seen keyed by `id`, while an entry
    // it couldn't resolve comes back as { userId, error: "Failed to fetch
    // Keycloak user ..." } — keyed by `userId` instead. Check both so a
    // successful lookup is never missed just because it isn't the shape the
    // most recently-seen error happened to use.
    const profilesById = new Map<string, any>(
      (data ?? []).map((profile: any) => [profile?.id ?? profile?.userId, profile]),
    );
    ids.forEach((userId) => {
      const name = buildDisplayName(profilesById.get(userId), userId);
      // Only cache a REAL resolution (name !== the raw id we asked for) —
      // an id the backend couldn't resolve either comes back missing from
      // the response entirely, or as an error-shaped entry ({ userId,
      // error }), and buildDisplayName falls back to echoing the id
      // unchanged in both cases. Caching that echo would make every future
      // lookup for this id (e.g. a newly-created waiver's createdBy, right
      // after persistNewWaiver, before the backend's own record has caught
      // up) see a cache "hit" and skip the network call forever — this
      // module-level cache lives for the whole session, so the only way to
      // recover was a full page refresh, which just gives it one more
      // chance to resolve, not a guaranteed fix.
      if (name !== userId) {
        nameCache.set(userId, name);
      }
      resolvers.get(userId)?.(name);
      inFlight.delete(userId);
    });
  } catch (err) {
    console.error(
      `Something went wrong while resolving user display names after ${LOOKUP_RETRY_COUNT} retries. ERROR:`,
      err,
    );
    ids.forEach((userId) => {
      resolvers.get(userId)?.(userId);
      inFlight.delete(userId);
    });
  }
};

const resolveUserDisplayName = (userId: string): Promise<string> => {
  const cached = nameCache.get(userId);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(userId);
  if (existing) return existing;

  const promise = new Promise<string>((resolve) => {
    pendingIds.push(userId);
    pendingResolvers.set(userId, resolve);
  });

  inFlight.set(userId, promise);

  if (!flushScheduled) {
    flushScheduled = true;
    flushTimeoutId = setTimeout(flushPendingLookups, 0);
  }

  return promise;
};

// Directly seeds the cache with a KNOWN-good name for one id — for the one
// case where the app already knows the answer without asking the backend:
// the current logged-in user's own id, whenever they're the one who just did
// something (created a waiver, sent a chat message, ...). Skips the
// getUserDetails round trip entirely rather than firing it and discarding
// the result, so their own name shows immediately with zero network calls,
// for every place their id shows up (not just the one action just taken).
// Only overwrites an id that isn't already cached, so it can never clobber
// a real resolved profile with a same-session guess if one already landed
// first. This module-level cache is cleared on refresh (fresh page load), so
// a refresh always re-resolves every id — including this one — for real,
// via the normal getUserDetails path, exactly like everything else.
export const seedUserDisplayName = (userId: string | undefined, displayName: string | undefined): void => {
  if (!userId || !displayName) return;
  if (nameCache.has(userId)) return;
  nameCache.set(userId, displayName);
};

// Warms the shared cache for a batch of ids ahead of any component actually
// needing them — e.g. every createdBy/responseBy/partyId across a loan's
// just-fetched waivers, called once right after that fetch resolves — so by
// the time WaiverSummary/WaiverChat render, names are typically already
// resolved instead of each one triggering its own lookup. Goes through the
// exact same queue/cache resolveUserDisplayName does, so this is a no-op for
// any id already cached or already in flight, and collapses into a single
// network call together with anything else queued in the same tick
// (including a component's own useUserDisplayName call, if one races this).
export const prefetchUserDisplayNames = (userIds: (string | undefined)[]): void => {
  userIds.forEach((userId) => {
    if (userId) resolveUserDisplayName(userId);
  });
};

// Resolves a Keycloak userId (e.g. a waiver's createdBy/responseBy) to
// "First Last" for display, falling back to the account's username or the
// raw id if the lookup fails or the profile has no name set. Shows the raw
// id immediately so the UI never blocks/blanks on this, then swaps in the
// resolved name once the lookup completes. Lookups for ids requested around
// the same time are batched into a single network call.
export const useUserDisplayName = (userId: string | undefined): string => {
  const [displayName, setDisplayName] = useState<string>(userId ?? "");

  useEffect(() => {
    if (!userId) {
      setDisplayName("");
      return;
    }

    const cached = nameCache.get(userId);
    if (cached) {
      setDisplayName(cached);
      return;
    }

    setDisplayName(userId);
    let isCurrent = true;
    resolveUserDisplayName(userId).then((name) => {
      if (isCurrent) setDisplayName(name);
    });
    return () => {
      isCurrent = false;
    };
  }, [userId]);

  return displayName;
};
